import { CommandPalette } from "@astryxdesign/core/CommandPalette";
import { HStack, StackItem } from "@astryxdesign/core/Stack";
import { Text } from "@astryxdesign/core/Text";
import { Token } from "@astryxdesign/core/Token";
import { createStaticSource, type SearchableItem } from "@astryxdesign/core/Typeahead";
import { useMemo } from "react";
import type { Idea } from "../../shared/types";
import { CATEGORY_META } from "../lib/format";

interface PaletteEntry extends SearchableItem {
  auxiliaryData: { idea: Idea };
}

interface Props {
  ideas: Idea[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (id: string) => void;
}

/** ⌘K: fuzzy-jump to any idea, open or done. */
export function IdeaPalette({ ideas, isOpen, onOpenChange, onPick }: Props) {
  const source = useMemo(() => {
    const entries: PaletteEntry[] = ideas.map((idea) => ({
      id: idea.id,
      label: idea.title,
      auxiliaryData: { idea },
    }));
    return createStaticSource(entries, {
      keywords: (item) => {
        const idea = item.auxiliaryData.idea;
        return [
          CATEGORY_META[idea.category].label,
          idea.status,
          ...idea.links.map((l) => l.domain),
          idea.notes.slice(0, 200),
        ];
      },
    });
  }, [ideas]);

  return (
    <CommandPalette<PaletteEntry>
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      searchSource={source}
      label="Jump to idea"
      emptyBootstrapText="Type to search your ideas"
      onValueChange={(id) => {
        onOpenChange(false);
        onPick(id);
      }}
      renderItem={(item) => {
        const idea = item.auxiliaryData.idea;
        const done = idea.status === "done";
        return (
          <HStack gap={1.5} align="center" width="100%">
            <Token size="sm" color={CATEGORY_META[idea.category].color} label={CATEGORY_META[idea.category].label} />
            <StackItem size="fill">
              <Text
                type="body"
                maxLines={1}
                hasStrikethrough={done}
                color={done ? "secondary" : "primary"}
              >
                {idea.title}
              </Text>
            </StackItem>
            {idea.links[0] ? (
              <Text type="supporting" size="xsm">
                {idea.links[0].domain}
              </Text>
            ) : null}
          </HStack>
        );
      }}
    />
  );
}
